/**
 * Output Sections Tooltips
 * Comprehensive tooltips for Dashboard, Financial Statements, KPIs, and other output sections
 * Phase E.1 Implementation
 */

export const outputTooltips = {
  // ============================================
  // DASHBOARD TOOLTIPS (20+ tooltips)
  // ============================================
  dashboard: {
    executive_dashboard: {
      title: "Executive Dashboard",
      content: "High-level overview of your business's financial performance and key metrics. This dashboard aggregates data from your strategic framework, cost management, and financial planning inputs to provide a comprehensive view of your business health.",
      example: "View revenue growth, profitability trends, and scenario comparisons at a glance."
    },
    revenue: {
      title: "Revenue",
      content: "Total income generated from all revenue streams during the selected period. Calculated from your Strategic Framework products and services.",
      example: "For a SaaS company: Monthly subscriptions ($50K) + One-time setup fees ($10K) = $60K total revenue."
    },
    revenue_growth: {
      title: "Revenue Growth (YoY)",
      content: "Year-over-year percentage change in revenue. A positive number indicates business growth, while negative indicates decline. Benchmark: 15-25% growth is considered healthy for growing businesses.",
      example: "If revenue was $100K last year and $120K this year, growth is +20%."
    },
    net_income: {
      title: "Net Income",
      content: "Bottom-line profit after all expenses, taxes, and costs are deducted from revenue. This is the actual profit your business keeps. Formula: Revenue - COGS - Operating Expenses - Interest - Taxes.",
      example: "Revenue $500K - COGS $200K - OpEx $150K - Interest $10K - Tax $42K = $98K net income."
    },
    net_margin: {
      title: "Net Profit Margin",
      content: "Percentage of revenue that becomes profit. Shows how efficiently your business converts sales into profit. Formula: (Net Income / Revenue) × 100. Benchmark: 10%+ is good, 20%+ is excellent.",
      example: "Net income $20K ÷ Revenue $100K = 20% net margin (excellent profitability)."
    },
    gross_margin: {
      title: "Gross Profit Margin",
      content: "Percentage of revenue remaining after deducting cost of goods sold (COGS). Indicates pricing power and production efficiency. Formula: (Revenue - COGS) / Revenue × 100. Benchmark: 50-70% for software, 20-40% for retail.",
      example: "Revenue $100K - COGS $30K = $70K gross profit → 70% gross margin (strong for software)."
    },
    ebitda_margin: {
      title: "EBITDA Margin",
      content: "Earnings Before Interest, Taxes, Depreciation, and Amortization as a percentage of revenue. Shows operational profitability before financing and accounting decisions. Benchmark: 15-25% is healthy.",
      example: "EBITDA $30K ÷ Revenue $100K = 30% EBITDA margin (excellent operational efficiency)."
    },
    scenario_display: {
      title: "Active Scenario",
      content: "Currently selected financial scenario for projections. Scenarios allow you to model different business outcomes: Realistic (baseline), Optimistic (best case), Pessimistic (worst case). You can create custom scenarios in the Scenario Modeling section.",
      example: "Viewing 'Optimistic' scenario shows +25% revenue growth vs 'Realistic' at +10%."
    },
    five_year_projection: {
      title: "5-Year Projection",
      content: "Financial forecasts covering the full planning horizon from your Setup configuration. Based on your planning season dates, growth rates, and strategic assumptions.",
      example: "Planning season July 2025 - June 2030 shows 5-year Ethiopian fiscal year projections."
    },
    revenue_net_income_trend: {
      title: "Revenue & Net Income Trend",
      content: "Historical and projected trends showing total revenue and net profit over time. Helps identify growth patterns, seasonality, and profitability trajectory. Diverging lines may indicate margin compression or expansion.",
      example: "Rising revenue with flat net income suggests increasing costs - review cost management."
    },
    profitability_margins: {
      title: "Profitability Margins Trend",
      content: "Tracks gross margin, EBITDA margin, and net margin over time. Stable or increasing margins indicate efficient scaling. Declining margins signal cost issues or pricing pressure.",
      example: "Gross margin holding at 70% while net margin drops from 20% to 15% suggests rising operating expenses."
    },
    cash_flow_analysis: {
      title: "Cash Flow Analysis",
      content: "Breakdown of cash movements: Operating (core business), Investing (assets/equipment), Financing (loans/equity). Positive operating cash flow is critical for sustainability.",
      example: "Operating CF +$50K (strong), Investing CF -$30K (growth investments), Financing CF +$20K (loan)."
    },
    planning_dashboard_note: {
      title: "Planning vs Actual Performance",
      content: "This dashboard shows planned/projected financial data based on your inputs. For actual performance tracking and variance analysis, use the Performance Tracking section to compare planned vs actual results.",
      example: "Compare planned revenue of $100K with actual revenue of $95K to identify 5% variance."
    }
  },

  // ============================================
  // FINANCIAL STATEMENTS TOOLTIPS (40+ tooltips)
  // ============================================
  financial_statements: {
    // Income Statement (P&L) Tooltips
    income_statement: {
      title: "Income Statement (P&L)",
      content: "Shows your business's profitability over a specific period by detailing all revenues and expenses. Also called Profit & Loss statement. This is one of the three core financial statements.",
      example: "Monthly P&L for January 2025 shows how profitable your business was during that month."
    },
    revenue: {
      title: "Revenue (Top Line)",
      content: "Total income from selling products/services before any deductions. Calculated from your Strategic Framework revenue streams. Also called 'Sales' or 'Turnover'. This is the starting point of your P&L.",
      example: "Subscription revenue $40K + Service revenue $15K + Product sales $10K = $65K total revenue."
    },
    cogs: {
      title: "Cost of Goods Sold (COGS)",
      content: "Direct costs attributable to producing products or delivering services. Includes materials, direct labor, and production overhead. Calculated from your Cost Management inputs. Formula: Beginning Inventory + Purchases - Ending Inventory.",
      example: "Software hosting $2K + API costs $1K + Customer support $3K = $6K COGS."
    },
    gross_profit: {
      title: "Gross Profit",
      content: "Revenue minus COGS. Represents profit before operating expenses. This shows how efficiently you produce/deliver your core products. Formula: Revenue - COGS. Higher is better.",
      example: "Revenue $100K - COGS $30K = $70K gross profit (70% gross margin - excellent!)."
    },
    operating_expenses: {
      title: "Operating Expenses (OpEx)",
      content: "Costs of running your business that aren't directly tied to production. Includes salaries, rent, marketing, utilities, software subscriptions. From your Cost Management operational costs.",
      example: "Salaries $25K + Rent $5K + Marketing $8K + Software $2K = $40K operating expenses."
    },
    ebitda: {
      title: "EBITDA",
      content: "Earnings Before Interest, Taxes, Depreciation, and Amortization. Shows operational profitability before financing decisions and non-cash expenses. Widely used for comparing companies. Formula: Gross Profit - Operating Expenses.",
      example: "Gross profit $70K - OpEx $40K = $30K EBITDA (30% EBITDA margin - very healthy)."
    },
    depreciation: {
      title: "Depreciation & Amortization",
      content: "Non-cash expense representing the decline in value of fixed assets (equipment, vehicles) and intangible assets over time. Spreads the cost of assets over their useful life. Calculated automatically based on your asset purchases.",
      example: "A $50K piece of equipment with 5-year life = $10K annual depreciation expense."
    },
    ebit: {
      title: "EBIT (Operating Profit)",
      content: "Earnings Before Interest and Taxes. Shows profit from core operations after depreciation but before financing costs and taxes. Also called 'Operating Income'. Formula: EBITDA - Depreciation.",
      example: "EBITDA $30K - Depreciation $5K = $25K EBIT (true operating profitability)."
    },
    interest_expense: {
      title: "Interest Expense",
      content: "Cost of borrowed money from loans and credit facilities. Automatically calculated from your Financial Management debt entries. This is a non-operating expense that reduces taxable income.",
      example: "$100K loan at 6% annual interest = $500 monthly interest expense. Integrated live from your loan records."
    },
    pbt: {
      title: "Profit Before Tax (PBT)",
      content: "Earnings before income taxes are deducted. Shows profitability before tax effects. Formula: EBIT - Interest Expense. This is your taxable income.",
      example: "EBIT $25K - Interest $2K = $23K PBT (this amount is subject to corporate tax)."
    },
    tax_expense: {
      title: "Tax Expense",
      content: "Income taxes owed to government based on PBT. Calculated using your tax rate from Setup configuration. Actual taxes may vary - consult your accountant.",
      example: "PBT $23K × 30% tax rate = $6,900 tax expense (varies by jurisdiction)."
    },
    net_income_pl: {
      title: "Net Income (Bottom Line)",
      content: "Final profit after ALL expenses including taxes. This is what your business actually earned. Also called 'Net Profit' or 'Net Earnings'. Formula: PBT - Tax Expense. This money can be reinvested or distributed to owners.",
      example: "PBT $23K - Tax $7K = $16K net income (the actual profit you keep)."
    },
    
    // Key Ratios
    key_ratios: {
      title: "Key Financial Ratios",
      content: "Important metrics derived from financial statements that show your business health, efficiency, and profitability at a glance. Compare these to industry benchmarks and track trends over time.",
      example: "Track gross margin 70%, EBITDA margin 30%, net margin 16% to monitor profitability trends."
    },
    gross_margin_ratio: {
      title: "Gross Margin %",
      content: "Percentage of revenue retained after COGS. Shows pricing power and production efficiency. Formula: (Revenue - COGS) / Revenue × 100. Industry benchmarks vary widely.",
      example: "70% for SaaS, 50% for e-commerce, 30% for retail, 20% for restaurants."
    },
    ebitda_margin_ratio: {
      title: "EBITDA Margin %",
      content: "Operational profitability as percentage of revenue. Shows efficiency before financing and tax decisions. Formula: EBITDA / Revenue × 100. Benchmark: 15-25% is healthy for most businesses.",
      example: "EBITDA $30K ÷ Revenue $100K = 30% (excellent operational performance)."
    },
    net_margin_ratio: {
      title: "Net Margin %",
      content: "Bottom-line profitability. Shows how much of each revenue dollar becomes profit. Formula: Net Income / Revenue × 100. Benchmark: 10%+ is good, 20%+ is excellent.",
      example: "Net income $16K ÷ Revenue $100K = 16% net margin (healthy profitability)."
    },
    operating_ratio: {
      title: "Operating Ratio %",
      content: "Operating expenses as percentage of revenue. Lower is better - shows cost efficiency. Formula: Operating Expenses / Revenue × 100. Benchmark: Below 80% is good, below 60% is excellent.",
      example: "OpEx $40K ÷ Revenue $100K = 40% (very efficient cost management)."
    },

    // Revenue by Stream
    revenue_by_stream: {
      title: "Revenue by Stream",
      content: "Breakdown of total revenue by Strategic Framework revenue streams. Shows contribution of each business line to total income. Helps identify high-performing streams and diversification level.",
      example: "Subscription Revenue 60% ($60K), Service Revenue 25% ($25K), Product Sales 15% ($15K)."
    },
    stream_breakdown: {
      title: "Stream Breakdown",
      content: "Detailed view of each revenue stream including linked products, revenue contribution, and percentage of total. Based on your Strategic Framework products and pricing.",
      example: "Enterprise SaaS stream: 3 products, $45K revenue, 45% of total (largest contributor)."
    },

    // Balance Sheet Tooltips
    balance_sheet: {
      title: "Balance Sheet",
      content: "Snapshot of your company's financial position at a specific point in time. Shows what you own (assets), what you owe (liabilities), and owner's equity. Must always balance: Assets = Liabilities + Equity.",
      example: "As of Dec 31, 2025: Total assets $500K = Liabilities $200K + Equity $300K."
    },
    cash: {
      title: "Cash & Cash Equivalents",
      content: "Liquid assets including bank account balances, money market funds, and short-term investments convertible to cash within 90 days. This is your most liquid asset - available for immediate use.",
      example: "Checking account $25K + Savings $50K + Money market $25K = $100K cash."
    },
    accounts_receivable: {
      title: "Accounts Receivable (A/R)",
      content: "Money owed to you by customers for products/services already delivered but not yet paid. Represents sales made on credit. Should be collected within 30-60 days typically.",
      example: "3 invoices totaling $15K due this month from customers (payment expected within 30 days)."
    },
    fixed_assets: {
      title: "Fixed Assets (PP&E)",
      content: "Long-term tangible assets used in operations: property, equipment, vehicles, computers. Shown at cost minus accumulated depreciation. These assets help generate revenue over multiple years.",
      example: "Office equipment $30K + Vehicles $40K + Computers $10K = $80K in fixed assets."
    },
    total_assets: {
      title: "Total Assets",
      content: "Everything your company owns that has economic value. Sum of current assets (cash, receivables) and long-term assets (equipment, property). Formula: Current Assets + Fixed Assets + Other Assets.",
      example: "Cash $100K + Receivables $50K + Fixed Assets $150K = $300K total assets."
    },
    accounts_payable: {
      title: "Accounts Payable (A/P)",
      content: "Money you owe suppliers and vendors for goods/services received but not yet paid. This is a current liability typically due within 30-60 days. Managing A/P well preserves cash flow.",
      example: "Supplier invoices totaling $12K due next month (manage cash flow to pay on time)."
    },
    total_liabilities: {
      title: "Total Liabilities",
      content: "Everything your company owes to others. Includes short-term debts (payables, current loan portions) and long-term debts (loans, mortgages). Integrated with Financial Management for real-time debt tracking.",
      example: "Accounts Payable $12K + Short-term Loan $20K + Long-term Debt $80K = $112K total liabilities. 🔗 Live data from your loan records."
    },
    retained_earnings: {
      title: "Retained Earnings",
      content: "Cumulative profits that have been reinvested in the business rather than distributed to owners. Grows with each year's net income. Formula: Prior Retained Earnings + Current Year Net Income - Dividends.",
      example: "Started with $50K, earned $20K this year, paid $5K dividends = $65K retained earnings."
    },
    total_equity: {
      title: "Total Equity (Net Worth)",
      content: "Owner's stake in the company after all liabilities are paid. Formula: Total Assets - Total Liabilities. Also called 'Net Worth' or 'Shareholder Equity'. Integrated with Financial Management for live equity tracking.",
      example: "Assets $300K - Liabilities $112K = $188K equity. 🔗 Live data includes shareholder investments."
    },

    // Cash Flow Statement Tooltips
    cash_flow: {
      title: "Cash Flow Statement",
      content: "Tracks actual cash moving in and out of your business. Different from P&L because it shows when cash changes hands, not when revenue is earned. Three categories: Operating, Investing, Financing.",
      example: "Even with $20K net income, negative cash flow can occur if customers haven't paid yet."
    },
    operating_cash_flow: {
      title: "Operating Cash Flow (OCF)",
      content: "Cash generated from core business operations. Shows if your business generates enough cash to sustain and grow operations. Formula: Net Income + Depreciation + Changes in Working Capital. Positive OCF is essential.",
      example: "Net income $20K + Depreciation $5K + Working capital changes -$3K = $22K operating cash flow (strong!)."
    },
    investing_cash_flow: {
      title: "Investing Cash Flow",
      content: "Cash used for or generated from investments in assets. Usually negative for growing businesses as they buy equipment and expand. Includes purchases/sales of PP&E, acquisitions, and investments.",
      example: "Bought new equipment -$30K + Sold old vehicle +$5K = -$25K investing cash flow (growth investments)."
    },
    financing_cash_flow: {
      title: "Financing Cash Flow",
      content: "Cash from or paid to lenders and investors. Includes loan proceeds, loan repayments, equity investments, and dividend payments. Positive when raising capital, negative when repaying debt.",
      example: "New loan +$50K - Loan repayment -$10K - Dividends -$5K = +$35K financing cash flow (net capital raised)."
    },
    net_cash_flow: {
      title: "Net Cash Flow",
      content: "Total change in cash position combining all three categories. Formula: Operating CF + Investing CF + Financing CF. Positive = cash increased, Negative = cash decreased. Track this carefully!",
      example: "Operating +$22K + Investing -$25K + Financing +$35K = +$32K net cash flow (strong cash position)."
    },
    cash_flow_breakdown: {
      title: "Cash Flow Breakdown",
      content: "Detailed components of operating cash flow showing how net income converts to actual cash. Includes non-cash expenses (depreciation) and working capital changes (receivables, payables).",
      example: "Net income $20K + Depreciation $5K (non-cash) + Working capital -$3K = $22K operating CF."
    },

    // Multi-Year Trend
    multi_year_trend: {
      title: "Revenue & Profitability Trends",
      content: "Historical and projected financial performance across all years in your planning horizon. Shows growth trajectory, profitability trends, and helps identify patterns or concerns.",
      example: "5-year view: Revenue growing from $100K (2025) to $200K (2029) = 15% CAGR (healthy growth)."
    }
  },

  // ============================================
  // KPIs DASHBOARD TOOLTIPS (50+ tooltips)
  // ============================================
  kpis: {
    // Overview Tooltips
    kpi_dashboard: {
      title: "Advanced KPI Dashboard",
      content: "Comprehensive business intelligence combining financial metrics, operational efficiency, growth indicators, and predictive analytics. Uses data from all your inputs to provide actionable insights.",
      example: "Monitor 25+ KPIs across 4 categories: Performance, Financial Health, Operational, and Forecasting."
    },
    
    // Key Metrics
    annual_revenue_kpi: {
      title: "Annual Revenue",
      content: "Total revenue projected or achieved over 12 months. Annualized from monthly/quarterly data. This is your top-line growth indicator.",
      example: "Monthly revenue $50K × 12 months = $600K annual revenue (annualized)."
    },
    annual_profit_kpi: {
      title: "Annual Net Profit",
      content: "Total net income over 12 months. Shows bottom-line profitability on an annual basis. Compare to revenue to calculate net margin.",
      example: "Monthly profit $8K × 12 months = $96K annual profit (16% net margin)."
    },
    profit_margin_kpi: {
      title: "Profit Margin",
      content: "Net income as a percentage of revenue. Key profitability indicator tracked over time. Benchmark: 10%+ good, 15%+ excellent, 20%+ outstanding.",
      example: "Net margin of 16% means you keep $16 of profit for every $100 in revenue."
    },
    business_health_score: {
      title: "Business Health Score",
      content: "Composite score (0-100) evaluating overall business performance across profitability, efficiency, growth, and sustainability. Scored on: Positive margins (60 pts), Revenue growth (20 pts), Cost efficiency (20 pts).",
      example: "Score 85/100 = Excellent health. Score 60/100 = Good but needs improvement."
    },
    yoy_growth: {
      title: "Year-over-Year Growth",
      content: "Percentage change compared to the same period last year. Key indicator of business momentum. Formula: (Current - Prior) / Prior × 100.",
      example: "+20% YoY revenue growth means you're earning 20% more than last year - strong performance!"
    },

    // Financial Management Integration
    wacc: {
      title: "WACC (Weighted Average Cost of Capital)",
      content: "Average rate your company pays for capital from all sources (debt and equity). Used to evaluate investment opportunities. Lower WACC = cheaper capital = better. Formula: (E/V × Cost of Equity) + (D/V × Cost of Debt × (1-Tax Rate)).",
      example: "WACC of 9.5% means new investments must return >9.5% to create value. 🔗 Live calculation from your loans and equity."
    },
    debt_to_equity: {
      title: "Debt-to-Equity Ratio",
      content: "Measures financial leverage by comparing total debt to shareholder equity. Shows capital structure and financial risk. Formula: Total Debt / Total Equity. Benchmark: <1.0 is conservative, 1.0-2.0 is moderate, >2.0 is aggressive.",
      example: "Debt-to-Equity of 0.8 means $0.80 of debt for every $1.00 of equity (moderate leverage). 🔗 Live data."
    },
    total_debt: {
      title: "Total Debt Outstanding",
      content: "Sum of all loans and credit facilities across all terms and types. Includes short-term and long-term debt. Tracked in real-time from Financial Management.",
      example: "Term loan $80K + Equipment financing $15K + Line of credit $10K = $105K total debt. 🔗 Live from loan records."
    },
    equity_value: {
      title: "Total Equity Value",
      content: "Current value of all shareholder equity including paid-in capital and retained earnings. Represents owner's stake. Tracked from Financial Management equity entries.",
      example: "Initial investment $100K + Retained earnings $50K + New equity $25K = $175K equity value. 🔗 Live data."
    },
    leverage_ratios: {
      title: "Leverage Ratios",
      content: "Metrics measuring your company's use of debt financing. Higher leverage = higher risk but potentially higher returns. Includes debt-to-equity, financial leverage, times interest earned, and DSCR.",
      example: "Monitor all 4 leverage ratios to ensure sustainable debt levels and adequate coverage."
    },
    times_interest_earned: {
      title: "Times Interest Earned (TIE)",
      content: "Measures ability to pay interest on debt. Formula: EBIT / Interest Expense. Shows how many times you can cover interest with operating profit. Benchmark: >3.0 is safe, <1.5 is concerning.",
      example: "EBIT $30K ÷ Interest $10K = 3.0× coverage (safe - can pay interest 3 times over)."
    },
    dscr: {
      title: "Debt Service Coverage Ratio (DSCR)",
      content: "Measures cash flow available to pay debt obligations. Formula: Operating Cash Flow / Total Debt Service. Lenders require >1.25. Below 1.0 means insufficient cash to pay debt.",
      example: "Operating CF $60K ÷ Debt payments $40K = 1.5 DSCR (healthy - exceeds minimum 1.25)."
    },
    return_on_equity: {
      title: "Return on Equity (ROE)",
      content: "Measures profitability relative to shareholder equity. Shows how effectively you're using invested capital. Formula: Net Income / Total Equity × 100. Benchmark: 15-20% is good.",
      example: "Net income $30K ÷ Equity $150K = 20% ROE (excellent return for shareholders)."
    },
    return_on_assets: {
      title: "Return on Assets (ROA)",
      content: "Measures how efficiently you're using assets to generate profit. Formula: Net Income / Total Assets × 100. Benchmark: 5-10% is typical, varies by industry.",
      example: "Net income $30K ÷ Total assets $250K = 12% ROA (efficient asset utilization)."
    },
    pe_ratio: {
      title: "Price-to-Earnings Ratio (P/E)",
      content: "Valuation metric comparing market value to earnings. Formula: Market Value / Net Income. Higher P/E suggests growth expectations. Public companies: 15-25 typical, 30+ = high growth.",
      example: "Market value $500K ÷ Net income $25K = 20 P/E (fair valuation)."
    },
    dividend_yield: {
      title: "Dividend Yield",
      content: "Annual dividends as percentage of equity value. Shows cash returns to shareholders. Formula: Annual Dividends / Equity Value × 100. Mature companies: 2-6%, growth companies: 0-2%.",
      example: "Dividends $10K ÷ Equity $200K = 5% yield (good return for mature business)."
    },

    // Performance Tab
    performance_analytics: {
      title: "Performance Analytics",
      content: "Comprehensive view of financial and operational performance combining revenue trends, profitability metrics, cost analysis, and efficiency indicators.",
      example: "Track 5-year revenue trajectory, margin evolution, and cost structure all in one view."
    },
    revenue_profitability_trend: {
      title: "Revenue & Profitability Trend",
      content: "Visual representation of revenue growth and profit margins over your planning horizon. Shows both absolute values (bars) and margin percentages (lines) to identify trends.",
      example: "Rising revenue bars with flat margin line = growing sales but margin compression (investigate costs)."
    },
    profitability_gauges: {
      title: "Profitability Metrics Gauges",
      content: "Visual indicators showing current profitability levels. Net margin gauge shows bottom-line efficiency. Health score gauge combines multiple factors for overall assessment.",
      example: "Net margin gauge at 16% in green zone = healthy profitability. Health score 85/100 = excellent."
    },
    cost_structure: {
      title: "Cost Structure Analysis",
      content: "Breakdown of total costs by category from Cost Management. Shows where money is spent and helps identify optimization opportunities. Pie chart shows proportional distribution.",
      example: "Salaries 40%, Marketing 25%, Rent 15%, Technology 10%, Other 10% (optimize marketing spend?)."
    },
    monthly_costs: {
      title: "Total Monthly Costs",
      content: "Sum of all recurring monthly costs from Cost Management including fixed, variable, and semi-variable costs. Used for cash flow planning and budgeting.",
      example: "$45K monthly costs = $540K annual cost projection (plan cash accordingly)."
    },
    annual_cost_projection: {
      title: "Annual Cost Projection",
      content: "Monthly costs multiplied by 12 to show annual cost burden. Helps with annual budgeting and comparing to annual revenue for efficiency analysis.",
      example: "Monthly $45K × 12 = $540K annual costs. With $600K revenue = 90% cost ratio (tight margins)."
    },

    // Financial Health Tab
    financial_health: {
      title: "Financial Health",
      content: "Key indicators of business sustainability: cash runway, break-even point, and safety margins. These metrics determine if your business can survive challenges and grow sustainably.",
      example: "12-month runway + positive cash flow + 20% safety margin = financially healthy business."
    },
    cash_runway: {
      title: "Cash Runway",
      content: "Months your business can operate with current cash before running out. Formula: Current Cash / Monthly Burn Rate. Benchmark: 12+ months is safe, 6-12 months is acceptable, <6 months is risky.",
      example: "Cash $120K ÷ Monthly burn $10K = 12-month runway (safe - time to reach profitability)."
    },
    break_even_units: {
      title: "Break-Even Units",
      content: "Number of units you must sell to cover all fixed and variable costs (zero profit). Formula: Fixed Costs / (Price - Variable Cost per Unit). Below this = loss, above this = profit.",
      example: "Fixed costs $50K ÷ Contribution margin $12.50/unit = 4,000 units to break even."
    },
    break_even_revenue: {
      title: "Break-Even Revenue",
      content: "Total sales revenue needed to cover all costs (zero profit/loss point). Formula: Break-Even Units × Price per Unit. This is your minimum revenue target.",
      example: "4,000 units × $100 price = $400K break-even revenue (must exceed to be profitable)."
    },
    margin_of_safety: {
      title: "Margin of Safety",
      content: "Percentage cushion above break-even point. Shows how much sales can drop before hitting break-even. Formula: (Actual Sales - Break-Even Sales) / Actual Sales × 100. Benchmark: 20%+ is comfortable.",
      example: "Sales $500K - Break-even $400K = $100K cushion ÷ $500K = 20% safety margin (good!)."
    },

    // Operational Efficiency Tab
    operational_efficiency: {
      title: "Operational Efficiency",
      content: "Metrics measuring how effectively your business uses resources to generate revenue. Includes cost efficiency, resource utilization, and performance benchmarks.",
      example: "Monitor cost per revenue dollar, operating ratios, and resource productivity."
    },
    cost_per_revenue_dollar: {
      title: "Cost per Revenue Dollar",
      content: "How much it costs to generate $1 of revenue. Formula: Total Costs / Total Revenue. Lower is better. Benchmark: <$0.80 is good, <$0.60 is excellent.",
      example: "$40K costs ÷ $100K revenue = $0.40 per dollar (excellent efficiency - 60% margin)."
    },
    operating_ratio_kpi: {
      title: "Operating Ratio",
      content: "Operating expenses as percentage of revenue. Measures cost control effectiveness. Formula: Operating Expenses / Revenue × 100. Benchmark: <70% is good, <50% is excellent.",
      example: "OpEx $40K ÷ Revenue $100K = 40% operating ratio (excellent cost management)."
    },
    cost_efficiency_score: {
      title: "Cost Efficiency Score",
      content: "Composite metric evaluating how well you control costs relative to revenue. Considers operating ratio, cost per revenue dollar, and cost growth rates. Scale: 0-100.",
      example: "Score 85/100 = Very efficient. Score 50/100 = Room for improvement in cost control."
    },
    resource_utilization: {
      title: "Resource Utilization",
      content: "Measures how effectively you're using business resources (assets, people, capacity). Higher utilization = better efficiency but watch for overextension. Benchmark: 75-90% is optimal.",
      example: "80% asset utilization = efficiently using equipment without being overextended."
    },
    department_efficiency: {
      title: "Department Efficiency Analysis",
      content: "Compares budget vs actual spending and efficiency scores across departments. Identifies high-performing departments and those needing optimization.",
      example: "Tech dept: 92% efficiency, under budget. Marketing: 78% efficiency, over budget (needs review)."
    },

    // Forecasting Tab
    forecasting: {
      title: "Forecasting & Predictions",
      content: "Forward-looking analysis using historical trends and scenario modeling to predict future performance. Includes revenue forecasts, growth predictions, and risk assessment.",
      example: "Predict next 24 months of revenue with confidence intervals based on historical growth."
    },
    revenue_forecast: {
      title: "Revenue Forecasting",
      content: "Predictive model projecting future revenue based on historical trends, growth rates, and seasonality. Includes confidence intervals (upper/lower bounds) showing forecast uncertainty.",
      example: "Next 12 months forecast: $600K ± 15% confidence interval ($510K - $690K range)."
    },
    forecast_confidence: {
      title: "Forecast Confidence Intervals",
      content: "Statistical range showing uncertainty in forecasts. Shaded area represents likely outcome range. Wider intervals = more uncertainty, narrower = more confidence.",
      example: "±15% confidence interval means 85% chance actual revenue will fall within the shaded range."
    },
    growth_predictions: {
      title: "Growth Predictions",
      content: "Forecasted business growth trajectory based on current trends and assumptions. Includes growth rate predictions, revenue targets, and milestone timelines.",
      example: "Current 20% YoY growth predicts reaching $1M revenue in 18 months if sustained."
    },
    predicted_growth_rate: {
      title: "Predicted Growth Rate",
      content: "Expected percentage growth in the next period based on historical trends and market conditions. Gauge shows relative growth level from low to high.",
      example: "15% predicted growth rate suggests healthy expansion but slower than past 20% rate."
    },
    revenue_target: {
      title: "12-Month Revenue Target",
      content: "Projected revenue for next 12 months based on current growth rate. This is your forecast goal. Compare actual results to this target monthly.",
      example: "Current $50K/month at 15% growth = $660K 12-month target (vs $600K current annual run rate)."
    },
    break_even_timeline: {
      title: "Break-Even Timeline",
      content: "Estimated months until you reach break-even point (profit = $0) based on current trajectory. Critical for startups and new ventures.",
      example: "Current burn rate suggests break-even in 8 months (April 2026) with projected growth."
    },
    profitability_forecast: {
      title: "Profitability Forecast",
      content: "Predicted net margin for next period based on expected revenue growth and cost trends. Shows if margins will improve or compress.",
      example: "Current 16% margin forecast to reach 19% next year (margin expansion from scale)."
    },
    risk_assessment: {
      title: "Risk Assessment",
      content: "Identifies and quantifies key business risks with probability and impact ratings. Helps prioritize risk mitigation efforts. Categories: Financial, operational, market, strategic.",
      example: "Cash flow risk: High probability (70%), High impact → Top priority for mitigation."
    },
    cash_flow_risk: {
      title: "Cash Flow Risk",
      content: "Risk of running out of cash before reaching profitability or next funding. Assessed based on runway, burn rate, and revenue growth. Red flags: <6 month runway, negative OCF, declining collections.",
      example: "5-month runway + negative cash flow = High risk (urgent action needed: cut costs or raise funds)."
    },
    market_competition_risk: {
      title: "Market Competition Risk",
      content: "Risk from competitive pressure, new entrants, or market saturation. Impacts pricing power, market share, and growth potential. Monitor competitor activity and market trends.",
      example: "Medium risk (45% probability) - 3 new competitors entered market but you have strong differentiation."
    },
    cost_inflation_risk: {
      title: "Cost Inflation Risk",
      content: "Risk of costs rising faster than revenue, compressing margins. Monitor cost trends, supplier pricing, labor costs, and inflation rates. Mitigate with efficiency improvements or price increases.",
      example: "Costs up 8% but revenue up 12% = Manageable. Costs up 15% but revenue flat = High risk."
    },
    revenue_concentration_risk: {
      title: "Revenue Concentration Risk",
      content: "Risk from over-reliance on few customers, products, or markets. Loss of major customer or product failure could devastate revenue. Diversification reduces this risk.",
      example: "Top customer = 40% of revenue = High concentration risk. Diversify to reduce dependency."
    },
    risk_probability: {
      title: "Risk Probability",
      content: "Likelihood of risk event occurring, expressed as percentage. Combined with impact to prioritize risk management. Higher probability = more likely to happen.",
      example: "75% probability × High impact = Critical risk requiring immediate mitigation plan."
    },
    risk_impact: {
      title: "Risk Impact",
      content: "Potential severity if risk occurs: Low (minor), Medium (significant), High (major), Critical (business-threatening). Combined with probability to determine priority.",
      example: "Medium probability + High impact = Moderate-High priority risk (monitor and plan mitigation)."
    }
  },

  // ============================================
  // ADVANCED CALCULATIONS TOOLTIPS (30+ tooltips)
  // ============================================
  advanced_calculations: {
    // Break-Even Analysis
    break_even_analysis: {
      title: "Break-Even Analysis",
      content: "Determines the sales level where total revenue equals total costs (zero profit). Critical for pricing decisions, target setting, and financial planning. Shows how many units or how much revenue needed to cover costs.",
      example: "Need to sell 4,000 units or generate $400K revenue to break even."
    },
    contribution_margin: {
      title: "Contribution Margin",
      content: "Amount each unit contributes to covering fixed costs and profit after variable costs. Formula: Price - Variable Cost per Unit. Higher contribution margin = faster break-even.",
      example: "Price $100 - Variable costs $60 = $40 contribution margin per unit."
    },
    contribution_margin_ratio: {
      title: "Contribution Margin Ratio",
      content: "Contribution margin as percentage of price. Shows what percent of each sale contributes to fixed costs and profit. Formula: Contribution Margin / Price × 100.",
      example: "$40 contribution ÷ $100 price = 40% contribution margin ratio."
    },

    // ROI Analysis
    roi_analysis: {
      title: "ROI Analysis",
      content: "Return on Investment analysis evaluates profitability of investments and projects. Compares costs vs benefits using NPV, IRR, and payback period. Helps prioritize capital allocation.",
      example: "$100K investment returning $150K over 3 years = 50% ROI, 2.5-year payback."
    },
    npv: {
      title: "Net Present Value (NPV)",
      content: "Present value of future cash flows minus initial investment. Positive NPV = good investment, negative = reject. Accounts for time value of money using discount rate.",
      example: "Invest $100K, get $120K in 2 years at 10% discount rate = $99.17K PV = -$0.83K NPV (marginally unprofitable)."
    },
    irr: {
      title: "Internal Rate of Return (IRR)",
      content: "Discount rate that makes NPV = $0. Compare to your required rate of return. IRR > required rate = good investment. Shows investment's effective rate of return.",
      example: "IRR of 18% vs required 12% = Good investment (exceeds hurdle rate by 6%)."
    },
    payback_period: {
      title: "Payback Period",
      content: "Time to recover initial investment from cash flows. Shorter is better. Simple metric but ignores time value of money and cash flows after payback.",
      example: "$100K investment with $40K annual cash flow = 2.5 years to payback."
    },

    // Cash Flow Projections
    cash_flow_projections: {
      title: "5-Year Cash Flow Projections",
      content: "Detailed forecasts of cash movements over planning horizon. Shows when cash shortages or surpluses occur. Essential for managing liquidity and planning financing needs.",
      example: "Year 1: Negative $50K (startup). Year 3: Positive $100K (profitable). Plan financing for Year 1-2."
    },
    operating_cash_flow_calc: {
      title: "Operating Cash Flow Calculation",
      content: "Cash from core business operations. Formula: Net Income + Depreciation + Changes in Working Capital. Positive OCF indicates business generates cash sustainably.",
      example: "Net income $50K + Depreciation $10K - Working capital increase $5K = $55K operating CF."
    },
    working_capital_changes: {
      title: "Working Capital Changes",
      content: "Changes in current assets and liabilities affecting cash. Increase in receivables or inventory = cash outflow. Increase in payables = cash inflow. Manages day-to-day liquidity.",
      example: "Receivables up $10K (customers owe more) = -$10K cash impact (money tied up)."
    },

    // WACC Calculation
    wacc_calculation: {
      title: "WACC Calculation",
      content: "Weighted Average Cost of Capital - your company's average cost of financing from all sources. Used as discount rate for investment decisions and company valuation. Lower is better.",
      example: "70% equity at 12% + 30% debt at 6% (after-tax) = 10.2% WACC (hurdle rate)."
    },
    cost_of_equity: {
      title: "Cost of Equity",
      content: "Return required by equity investors. Higher than debt cost because equity is riskier. Estimated using CAPM or comparable company analysis. Typically 10-15% for small businesses.",
      example: "Investors require 12% return on equity (their opportunity cost)."
    },
    cost_of_debt: {
      title: "Cost of Debt",
      content: "Average interest rate on all debt after tax benefit. Formula: Interest Rate × (1 - Tax Rate). Debt is cheaper than equity because interest is tax-deductible.",
      example: "Loan at 8% interest with 25% tax rate = 6% after-tax cost of debt."
    },
    capital_structure: {
      title: "Capital Structure",
      content: "Mix of debt and equity financing. Shown as percentages of total capital. Optimal structure balances cost of capital with financial risk.",
      example: "70% equity + 30% debt = Conservative structure (low risk, higher WACC)."
    }
  },

  // ============================================
  // EXPORT & REPORTS TOOLTIPS (15+ tooltips)
  // ============================================
  export_reports: {
    export_manager: {
      title: "Export & Reports Manager",
      content: "Comprehensive export system for generating business plan documents in multiple formats. Subscription-tiered access with usage tracking. Create professional reports for investors, lenders, or internal planning.",
      example: "Professional tier: 5 PDF exports per month + Excel + individual chart downloads."
    },
    export_permissions: {
      title: "Export Permissions",
      content: "Your subscription tier determines available export formats, frequency limits, and features. Starter: PDF only (1/month), Professional: PDF+Excel+Charts (5/month), Business: Unlimited all formats.",
      example: "Upgrade to Business tier for unlimited PDF, Excel, PowerPoint exports with custom branding."
    },
    comprehensive_export: {
      title: "Comprehensive Business Plan Export",
      content: "Complete business plan document including executive summary, financial statements, strategic framework, cost analysis, and projections. Professional PDF format suitable for investors and lenders.",
      example: "Generate 50-page business plan PDF with all financials, strategy, and projections."
    },
    individual_charts: {
      title: "Individual Chart Downloads",
      content: "Export specific charts and visualizations as images (PNG) for presentations or reports. Available on Professional tier and above. High-quality images suitable for printing.",
      example: "Download revenue trend chart as PNG for PowerPoint presentation to board."
    },
    performance_reports: {
      title: "Performance Reports",
      content: "Generate specialized reports: Plan vs Actual variance analysis, KPI dashboards, financial ratios, or custom date ranges. Available on Business tier and above.",
      example: "Q4 performance report comparing planned $250K revenue vs actual $275K (+10%)."
    },
    export_history: {
      title: "Export History",
      content: "Track all your exports with timestamps, formats, and download links. Re-download previous exports without counting against monthly limit. Retained for 90 days.",
      example: "Re-download investor deck from last month without using this month's quota."
    },
    excel_enhanced: {
      title: "Enhanced Excel Export",
      content: "Multi-sheet Excel workbook with: Financial statements (3 sheets), Strategic framework data, Cost breakdown, KPI calculations, Chart data tables. Fully editable for custom analysis.",
      example: "Excel workbook with 8 sheets containing all your business data plus formulas for what-if analysis."
    }
  },

  // ============================================
  // PERFORMANCE TRACKING TOOLTIPS (25+ tooltips)
  // ============================================
  performance_tracking: {
    plan_vs_actual: {
      title: "Plan vs Actual Analysis",
      content: "Compares planned projections to actual results to identify variances. Essential for accountability, learning, and course correction. Shows where you're exceeding or missing targets.",
      example: "Planned Q1 revenue $100K vs actual $95K = -5% variance (analyze why and adjust)."
    },
    variance_analysis: {
      title: "Variance Analysis",
      content: "Detailed examination of differences between planned and actual results. Positive variance = better than plan, negative = worse. Analyze causes: market, execution, or planning assumptions.",
      example: "Revenue variance -$5K (worse) + Cost variance +$3K savings (better) = -$2K net variance."
    },
    variance_percentage: {
      title: "Variance Percentage",
      content: "Variance expressed as percentage of plan. Makes comparisons easier across different scales. Formula: (Actual - Plan) / Plan × 100. Benchmark: ±10% is typical, ±20%+ needs investigation.",
      example: "Actual $95K vs Plan $100K = -5% variance (acceptable range, monitor trend)."
    },
    actual_data_recording: {
      title: "Actual Data Recording",
      content: "Enter real results for sales, expenses, headcount, and other metrics. Compare to projections to measure performance. Regular recording enables trend analysis and accurate forecasting.",
      example: "Record actual January sales $47K vs projected $50K to track performance."
    },
    performance_kpis: {
      title: "Performance KPIs",
      content: "Key metrics comparing actual results to targets across multiple dimensions. Shows performance grades (on-track, at-risk, behind) for each KPI. Actionable dashboard for management.",
      example: "Revenue: On Track (98% of target), Costs: At Risk (105% of budget), Headcount: On Track."
    },
    achievement_rate: {
      title: "Achievement Rate",
      content: "Percentage of target achieved. Formula: (Actual / Target) × 100. 100%+ = exceeding target, 90-100% = on track, <90% = behind. Use to assess performance quality.",
      example: "Achieved $95K of $100K target = 95% achievement rate (on track but slightly behind)."
    }
  }
};

// Helper function to get tooltip by path (e.g., "dashboard.revenue")
export const getTooltip = (path) => {
  const keys = path.split('.');
  let tooltip = outputTooltips;
  
  for (const key of keys) {
    tooltip = tooltip[key];
    if (!tooltip) return null;
  }
  
  return tooltip;
};

export default outputTooltips;
